import React, { ReactElement, useCallback } from 'react';
import Editor, {
    BtnBold,
    BtnBulletList,
    BtnItalic,
    BtnLink,
    BtnNumberedList,
    BtnUnderline,
    ContentEditableEvent,
    Separator,
    Toolbar,
} from 'react-simple-wysiwyg';
import DOMPurify from 'dompurify';

export default function WysiwygEditor(props: {
    bold?: boolean;
    italic?: boolean;
    link?: boolean;
    onChange: (content: string) => void;
    orderedList?: boolean;
    underline?: boolean;
    unorderedList?: boolean;
    value: string;
}): ReactElement {
    const {
        bold = false,
        italic = false,
        link = false,
        onChange,
        orderedList = false,
        underline = false,
        unorderedList = false,
        value,
    } = props;

    const initialContent: string = (!value || value.trim() === '') ? '<p><br></p>' : value;

    const onChangeEditorContent: (content: ContentEditableEvent) => void = useCallback((content: ContentEditableEvent): void => {
        // Sanitise to remove any styles added while copy - pasting
        let cleanHtml: string = DOMPurify.sanitize(content.target.value, {
            ALLOWED_ATTR: [ 'href' ],
            ALLOWED_TAGS: [
                'b', 'i', 'u', 'p', 'ul', 'ol', 'li', 'br', 'strong', 'em', 'div', 'a',
            ],
        });

        // Force all links to open in a new tab.
        // The editor's link button only sets href, and DOMPurify strips target/rel, so we add them here after sanitising.
        const doc: Document = new DOMParser().parseFromString(cleanHtml, 'text/html');
        doc.querySelectorAll<HTMLAnchorElement>('a[href]').forEach((a: HTMLAnchorElement): void => {
            a.setAttribute('target', '_blank');
            a.setAttribute('rel', 'noopener noreferrer');
        });
        cleanHtml = doc.body.innerHTML;

        // Remove only <p></p> tags with no spaces inside, since they are added randomly.
        cleanHtml = cleanHtml.replace(/<p><\/p>/g, '');

        onChange(cleanHtml);
    }, [
        onChange,
    ]);

    const hasInlineFormatting: boolean = bold || italic || underline;
    const hasListFormatting: boolean = unorderedList || orderedList;

    const toolbarBlock: JSX.Element = (hasInlineFormatting || hasListFormatting || link) && (
        <Toolbar>
            {bold && <BtnBold />}
            {italic && <BtnItalic />}
            {underline && <BtnUnderline />}

            {hasInlineFormatting && hasListFormatting && <Separator />}
            {unorderedList && <BtnBulletList />}
            {orderedList && <BtnNumberedList />}

            {(hasInlineFormatting || hasListFormatting) && link && <Separator />}
            {link && <BtnLink />}
        </Toolbar>
    );

    return (
        <Editor
            containerProps={{ className: 'wysiwyg-editor' }}
            onChange={onChangeEditorContent}
            value={initialContent}
        >
            {toolbarBlock}
        </Editor>
    );
}
